import { useCallback, useEffect, useState } from 'react'
import { can } from '../../lib/api'
import { exitApi, type ExitItemState, type ExitPlan, type ExitPlanView } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { Dialog, Status, Timeline, Toggletip, type Tone } from '../kit'
import { DateRuleNote } from '../DateRuleNote'
import { checkNotAfterToday, todayIso, useLeftFields } from '../../lib/dateRules'
import { dayMonthYear, stamp, todayISO, truncate } from './words'

// The exit plan, and where the exit stands against the calendar.
//
// # WHERE THE DATES COME FROM
//
// Only from what is recorded: the day the exit started and closed, the plan's
// end date as GET /v1/exits works it out, and the dates written into the
// evidence (the notice served, the transition completed, the data destroyed,
// the backups' expiry). A milestone nobody recorded is not drawn, and with
// fewer than two dates there is no line to draw at all.
//
// The plan itself, its scenarios and the reasoning in them, opens in a drawer;
// the panel shows its standing and the four facts people ask for.

const STANDING: Record<string, { tone: Tone; text: string }> = {
  CURRENT: { tone: 'ok', text: 'Current' },
  STALE: { tone: 'warn', text: 'Out of date' },
  MISSING: { tone: 'crit', text: 'Missing' },
}

function emptyPlan(): ExitPlan {
  return {
    triggers: [],
    scenarios: [
      { kind: 'PLANNED', approach: '', targetDays: 90 },
      { kind: 'STRESSED', approach: '', targetDays: 14 },
    ],
    noAlternate: false,
  }
}

export type PlanDates = {
  openedAt?: string
  closedAt?: string
  dueOn?: string
  items: ExitItemState[]
}

function milestones(d: PlanDates, today: string) {
  const out: { key: string; label: string; date: string }[] = []
  const day = (iso: string | undefined) => (iso ? iso.slice(0, 10) : '')
  const ev = (key: string) => d.items.find((i) => i.key === key && i.done)?.evidence
  if (d.openedAt) out.push({ key: 'opened', label: 'Offboarding started', date: day(d.openedAt) })
  const notice = ev('CONTRACT_TERMINATED')?.servedOn
  if (notice) out.push({ key: 'notice', label: 'Notice served', date: notice })
  const moved = ev('DATA_RETURNED')?.transitionCompletedOn
  if (moved) out.push({ key: 'moved', label: 'Service moved', date: moved })
  const destroyed = ev('DATA_DELETED')?.deletedOn
  if (destroyed) out.push({ key: 'destroyed', label: 'Data destroyed', date: destroyed })
  const backups = ev('BACKUPS_ADDRESSED')?.retainedUntil
  if (backups) out.push({ key: 'backups', label: 'Backups expire', date: backups })
  if (d.closedAt) out.push({ key: 'closed', label: 'Closed', date: day(d.closedAt) })
  else if (d.dueOn) out.push({ key: 'due', label: 'Planned end', date: d.dueOn })
  return out.filter((m) => /^\d{4}-\d{2}-\d{2}$/.test(m.date)).map((m) => ({ ...m, passed: m.date <= today }))
}

export function ExitPlanPanel({ vendorId, dates, onSaved }: { vendorId: string; dates: PlanDates; onSaved: () => void }) {
  const [view, setView] = useState<ExitPlanView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [reading, setReading] = useState(false)
  const [editing, setEditing] = useState(false)
  const names = usePeopleNames()

  const load = useCallback(() => {
    exitApi
      .exitPlan(vendorId)
      .then((v) => {
        setView(v)
        setError(null)
      })
      .catch(setError)
  }, [vendorId])
  useEffect(load, [load])

  const today = todayISO()
  const miles = milestones(dates, today)

  if (error != null) return <ErrorBox error={error} what="The exit plan" />
  if (view == null) return null

  const pos = view.position
  const plan = pos.plan
  const standing = !pos.material && pos.standing === 'MISSING' ? { tone: 'neutral' as Tone, text: 'Not required' } : STANDING[pos.standing]
  const days = (kind: string) => plan?.scenarios.find((s) => s.kind === kind)?.targetDays

  return (
    <section className="panel exit-panel" aria-labelledby="exit-plan-h">
      <div className="exit-panel-head">
        <h2 id="exit-plan-h" className="exit-grow">
          Exit plan
        </h2>
        {standing && <Status tone={standing.tone}>{standing.text}</Status>}
        <Toggletip label="About the exit plan">{truncate(pos.detail, 200)}</Toggletip>
      </div>

      {miles.length >= 2 && <Timeline milestones={miles} today={today} />}

      {plan ? (
        <dl className="exit-facts">
          <div>
            <dt>Planned exit</dt>
            <dd>{days('PLANNED') ? days('PLANNED') + ' days' : 'Not set'}</dd>
          </div>
          <div>
            <dt>Stressed exit</dt>
            <dd>{days('STRESSED') ? days('STRESSED') + ' days' : 'Not set'}</dd>
          </div>
          <div>
            <dt>Alternate</dt>
            <dd title={plan.noAlternate ? plan.alternateNote : plan.alternateProvider}>
              {plan.noAlternate ? 'None' : truncate(plan.alternateProvider ?? 'Not named', 28)}
            </dd>
          </div>
          <div>
            <dt>Last tested</dt>
            <dd>{plan.lastTestedOn ? dayMonthYear(plan.lastTestedOn) : 'Never'}</dd>
          </div>
        </dl>
      ) : (
        <p className="exit-sub">No plan is recorded for this vendor</p>
      )}

      <div className="exit-row">
        {plan && (
          <button type="button" className="exit-link" onClick={() => setReading(true)}>
            Read the plan
          </button>
        )}
        {can('vendor.edit') && (
          <button type="button" className="exit-link" onClick={() => setEditing(true)}>
            {plan ? 'Revise the plan' : 'Record the plan'}
          </button>
        )}
      </div>

      <Dialog shape="drawer" kind="record" open={reading} onClose={() => setReading(false)} title="Exit plan">
        {plan && (
          <div className="exit-drawer-stack">
            <p className="exit-para">
              {pos.detail} {pos.materialBasis}
            </p>
            <h3 className="exit-h3">What would start an exit</h3>
            <ul className="exit-bullets">
              {plan.triggers.map((t) => (
                <li key={t}>{view.vocabulary.triggers.find((v) => v.code === t)?.name ?? t}</li>
              ))}
            </ul>
            {plan.scenarios.map((s) => (
              <div key={s.kind}>
                <h3 className="exit-h3">{view.vocabulary.scenarioKinds.find((k) => k.code === s.kind)?.name ?? s.kind}</h3>
                <p className="exit-para">
                  {s.approach} Off the vendor within {s.targetDays} days.
                </p>
              </div>
            ))}
            <h3 className="exit-h3">Alternate provider</h3>
            <p className="exit-para">{plan.noAlternate ? 'None. ' + (plan.alternateNote ?? '') : plan.alternateProvider}</p>
            <h3 className="exit-h3">Last tested</h3>
            <p className="exit-para">{plan.lastTestedOn ? dayMonthYear(plan.lastTestedOn) + '. ' + (plan.testSummary ?? '') : 'Never'}</p>
            <p className="exit-sub">
              Recorded by {personLabel(names, plan.recordedBy)}, {stamp(plan.recordedAt)}, revision {view.revisions.length}
            </p>
          </div>
        )}
      </Dialog>

      <PlanForm
        vendorId={vendorId}
        open={editing}
        view={view}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false)
          load()
          onSaved()
        }}
      />
    </section>
  )
}

function PlanForm({
  vendorId,
  open,
  view,
  onClose,
  onSaved,
}: {
  vendorId: string
  open: boolean
  view: ExitPlanView
  onClose: () => void
  onSaved: () => void
}) {
  const [plan, setPlan] = useState<ExitPlan>(emptyPlan)
  const current = view.position.plan
  const save = useSave(open, current ? 'Revise the exit plan' : 'Record the exit plan')
  const busy = save.busy
  const [err, setErr] = useState<unknown>(null)
  // D23 of the date rules: a test is recorded once it has happened.
  const left = useLeftFields()
  const testedRule = checkNotAfterToday('Last tested on', plan.lastTestedOn ?? '', todayIso())

  // Each opening starts from the plan as it stands.
  useEffect(() => {
    if (!open) return
    setErr(null)
    setPlan(
      current
        ? {
            triggers: [...current.triggers],
            scenarios: current.scenarios.length > 0 ? current.scenarios.map((s) => ({ ...s })) : emptyPlan().scenarios,
            alternateProvider: current.alternateProvider ?? '',
            noAlternate: current.noAlternate,
            alternateNote: current.alternateNote ?? '',
            lastTestedOn: current.lastTestedOn ?? '',
            testSummary: current.testSummary ?? '',
          }
        : emptyPlan(),
    )
  }, [open, current])

  function send() {
    if (testedRule) {
      left.leave('lastTestedOn')
      return
    }
    setErr(null)
    save.send(exitApi.saveExitPlan(vendorId, plan), { done: onSaved, failed: setErr })
  }


  function scenario(kind: string) {
    return plan.scenarios.find((s) => s.kind === kind) ?? { kind, approach: '', targetDays: 0 }
  }
  function setScenario(kind: string, patch: Partial<{ approach: string; targetDays: number }>) {
    const exists = plan.scenarios.some((s) => s.kind === kind)
    const scenarios = exists
      ? plan.scenarios.map((s) => (s.kind === kind ? { ...s, ...patch } : s))
      : [...plan.scenarios, { kind, approach: '', targetDays: 0, ...patch }]
    setPlan({ ...plan, scenarios })
  }

  return (
    <Dialog
      shape="drawer"
      kind="form"
      open={open}
      onClose={onClose}
      title={current ? 'Revise the exit plan' : 'Record the exit plan'}
      busy={busy}
      primary={{ label: busy ? 'Saving' : 'Save the plan', onClick: send, actionId: 'tprm.exit-workspace.a05' }}
    >
      <div className="exit-drawer-stack">
        {err != null && <ErrorBox error={err} what="The exit plan" verb="saved" />}
        <fieldset className="fieldset">
          <legend>What would start an exit</legend>
          <div className="exit-checks">
            {view.vocabulary.triggers.map((t) => {
              const id = 'plan-trigger-' + t.code
              const on = plan.triggers.includes(t.code)
              return (
                <div key={t.code} className="check">
                  <input
                    id={id}
                    type="checkbox"
                    checked={on}
                    onChange={() =>
                      setPlan({ ...plan, triggers: on ? plan.triggers.filter((x) => x !== t.code) : [...plan.triggers, t.code] })
                    }
                  />
                  <label htmlFor={id}>{t.name}</label>
                </div>
              )
            })}
          </div>
        </fieldset>

        {view.vocabulary.scenarioKinds.map((k) => {
          const s = scenario(k.code)
          return (
            <div key={k.code} className="exit-drawer-stack">
              <div className="field">
                <label htmlFor={'plan-approach-' + k.code}>{k.name}</label>
                <textarea
                  id={'plan-approach-' + k.code}
                  rows={2}
                  value={s.approach}
                  onChange={(e) => setScenario(k.code, { approach: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor={'plan-days-' + k.code}>Days to be off the vendor</label>
                <input
                  id={'plan-days-' + k.code}
                  type="number"
                  min={1}
                  value={s.targetDays || ''}
                  onChange={(e) => setScenario(k.code, { targetDays: Number(e.target.value) })}
                />
              </div>
            </div>
          )
        })}

        <div className="field">
          <label htmlFor="plan-alternate">Alternate provider</label>
          <input
            id="plan-alternate"
            type="text"
            disabled={plan.noAlternate}
            value={plan.alternateProvider ?? ''}
            onChange={(e) => setPlan({ ...plan, alternateProvider: e.target.value })}
          />
        </div>
        <div className="check">
          <input
            id="plan-no-alternate"
            type="checkbox"
            checked={plan.noAlternate}
            onChange={(e) => setPlan({ ...plan, noAlternate: e.target.checked, alternateProvider: '' })}
          />
          <label htmlFor="plan-no-alternate">There is no alternate</label>
        </div>
        {plan.noAlternate && (
          <div className="field">
            <label htmlFor="plan-alt-note">Why there is none, and the fallback</label>
            <textarea
              id="plan-alt-note"
              rows={2}
              value={plan.alternateNote ?? ''}
              onChange={(e) => setPlan({ ...plan, alternateNote: e.target.value })}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="plan-tested">Last tested on</label>
          <input
            id="plan-tested"
            type="date"
            value={plan.lastTestedOn ?? ''}
            onChange={(e) => setPlan({ ...plan, lastTestedOn: e.target.value })}
            onBlur={() => left.leave('lastTestedOn')}
            aria-invalid={left.shown('lastTestedOn', testedRule) ? true : undefined}
            aria-describedby={left.shown('lastTestedOn', testedRule) ? 'plan-tested-rule' : undefined}
          />
          <DateRuleNote id="plan-tested-rule" message={left.shown('lastTestedOn', testedRule)} />
        </div>
        <div className="field">
          <label htmlFor="plan-test-summary">What the test showed</label>
          <input
            id="plan-test-summary"
            type="text"
            value={plan.testSummary ?? ''}
            onChange={(e) => setPlan({ ...plan, testSummary: e.target.value })}
          />
        </div>
      </div>
    </Dialog>
  )
}
